import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type {
  ReleaseDetail,
  ReleaseValidationCheck,
  ValidationCategory,
  ValidationStatus,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";
import { VALIDATION_STATUS_LABELS } from "./releaseVocabulary";

const CATEGORIES: ValidationCategory[] = [
  "APPLICATION_HEALTH", "SMOKE_TEST", "DATABASE", "API", "UI", "QUEUE", "INTEGRATION",
  "MONITORING", "LOGS", "PERFORMANCE", "SECURITY", "BUSINESS_VALIDATION", "CUSTOMER_VALIDATION",
];

/**
 * Adding a post-deployment check, or recording how one went (§40).
 *
 * <p>A failed check demands a reason. It is the worst possible row for an incident review to
 * find blank: it proves somebody looked and noticed, and records nothing about what they saw.
 */
export function ValidationCheckDrawer({
  release,
  check,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  check: ReleaseValidationCheck | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const recording = check != null;
  const [category, setCategory] = useState<ValidationCategory>("APPLICATION_HEALTH");
  const [checkName, setCheckName] = useState("");
  const [description, setDescription] = useState("");
  const [mandatory, setMandatory] = useState(true);
  const [status, setStatus] = useState<ValidationStatus>("PASSED");
  const [result, setResult] = useState("");
  const [failureReason, setFailureReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (!open) {
      return;
    }
    setCategory(check?.category ?? "APPLICATION_HEALTH");
    setCheckName(check?.checkName ?? "");
    setDescription(check?.description ?? "");
    setMandatory(check?.mandatory ?? true);
    setStatus(check?.status === "NOT_STARTED" ? "PASSED" : check?.status ?? "PASSED");
    setResult(check?.result ?? "");
    setFailureReason(check?.failureReason ?? "");
    setEvidence(check?.evidenceReference ?? "");
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, check]);

  const needsReason = recording && status === "FAILED";
  const canSubmit = recording
    ? !needsReason || failureReason.trim() !== ""
    : checkName.trim() !== "";

  const submit = () =>
    void run(
      () =>
        recording
          ? api.put(`/api/v1/releases/${release.id}/validation/${check.id}/result`, {
              status,
              result: result.trim() === "" ? null : result.trim(),
              evidenceReference: evidence.trim() === "" ? null : evidence.trim(),
              failureReason: failureReason.trim() === "" ? null : failureReason.trim(),
              lockVersion: check.lockVersion,
            })
          : api.post(`/api/v1/releases/${release.id}/validation`, {
              category,
              checkName: checkName.trim(),
              description: description.trim() === "" ? null : description.trim(),
              ownerId: null,
              mandatory,
              lockVersion: null,
            }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={recording ? "Record the check" : "Add a check"}
      subtitle={recording ? check.checkName : "Something that says the release actually worked."}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : recording ? "Record" : "Add check"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        {recording ? (
          <>
            <Field label="Result" required>
              <Select
                value={status}
                onChange={(event) => setStatus(event.target.value as ValidationStatus)}
              >
                {Object.entries(VALIDATION_STATUS_LABELS)
                  .filter(([value]) => value !== "NOT_STARTED")
                  .map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
              </Select>
            </Field>

            {needsReason ? (
              <Field
                label="What was wrong"
                required
                hint="A failed check with nothing beside it proves somebody noticed and records nothing."
              >
                <Textarea
                  rows={3}
                  autoFocus
                  value={failureReason}
                  onChange={(event) => setFailureReason(event.target.value)}
                />
              </Field>
            ) : (
              <Field label="What you saw" hint="Optional — a response time, a status code, a count.">
                <Textarea
                  rows={3}
                  value={result}
                  onChange={(event) => setResult(event.target.value)}
                />
              </Field>
            )}

            <Field label="Evidence" hint="A link to the dashboard, log or screenshot.">
              <Input value={evidence} onChange={(event) => setEvidence(event.target.value)} />
            </Field>
          </>
        ) : (
          <>
            <Field label="What is being checked" required>
              <Input
                autoFocus
                value={checkName}
                placeholder="Sign-in smoke test"
                onChange={(event) => setCheckName(event.target.value)}
              />
            </Field>
            <Field label="Category" required>
              <Select
                value={category}
                onChange={(event) => setCategory(event.target.value as ValidationCategory)}
              >
                {CATEGORIES.map((value) => (
                  <option key={value} value={value}>
                    {value.replace(/_/g, " ").toLowerCase()}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="How to check it">
              <Textarea
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Field>
            <label className="flex items-center gap-2 text-sm text-ink-800">
              <input
                type="checkbox"
                checked={mandatory}
                onChange={(event) => setMandatory(event.target.checked)}
              />
              Mandatory — the release is not finished until this passes
            </label>
          </>
        )}
      </div>
    </Drawer>
  );
}
